import { lighten } from 'polished';
import React, { FC } from 'react';
import { hoverOpacity, ImageContainer } from 'src/shared/globals';
import MdCheckCircle from 'src/shared/icons/check-circle-solid.svg';
import NoImagePng from 'assets/images/others/no-image.png';
import styled from 'styled-components';
import { cutString, T } from '../../../shared/helpers';
import { BadgeBlue } from '../badge';
import { Icon } from '../icon';
import { CatalogListItemButton, CatalogListItemModel } from './catalogListItemModel';

const Container = styled.div<{ $isDisabled?: boolean }>`
    max-width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};

    ${ImageContainer} {
        margin-bottom: 10px;
        aspect-ratio: 1;

        img {
            ${hoverOpacity}
        }
    }
    ${(props) => props.$isDisabled && `opacity: 0.3`};
`;

const CheckIcon = styled(Icon)`
    position: absolute;
    right: 10px;
    top: 10px;
    width: 24px;
    height: 24px;
    color: ${(props) => props.theme.app.secondary};
    stroke-width: 0;
`;

const CatalogListItemFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CatalogListItemFooterText = styled.div<{ $isConfiguratedInZakeke?: boolean; $hideAlreadyConfiguredBadge?: boolean }>`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    span {
        font-size: 12px;
        font-family: ${(props) => props.theme.app.font};
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: ${(props) => (props.$isConfiguratedInZakeke && !props.$hideAlreadyConfiguredBadge ? 'not-allowed' : 'default')};
    }
`;

const FooterButtons = styled.ul`
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    align-items: center;
`;

const FooterButton = styled.li`
    margin-left: 5px;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
`;

const ButtonIcon = styled(Icon)<{ $isActive?: boolean }>`
    width: 18px;
    height: 18px;
    stroke-width: 0px;
    color: ${(props) => props.theme.app.component.icon.button.default.color};

    ${(props) =>
        props.$isActive &&
        `
            color: ${props.theme.app.secondary};
        `}
`;

interface CatalogListItemProps {
    model: CatalogListItemModel;
    selected: boolean;
    onClick: (hideAlreadyConfiguredBadge?: boolean) => void;
    onCatalogListItemButtonClick: (button: CatalogListItemButton, model: CatalogListItemModel) => void;
    hideAlreadyConfiguredBadge?: boolean;
    disableProductCode?: string;
}

const AlreadyImportedBadge = styled(BadgeBlue)`
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${lighten(0.3, '#99cf16')};
`;

// Item component
export const CatalogListItem: FC<CatalogListItemProps> = ({
    model,
    selected,
    onClick,
    onCatalogListItemButtonClick,
    hideAlreadyConfiguredBadge,
    disableProductCode
}) => {
    let imagePreview: React.ReactNode = <img src={NoImagePng} alt='' style={{ opacity: 0.5 }} />;

    if (model.thumbnail) imagePreview = <img loading={'lazy'} src={model.thumbnail} alt={model.title || ''} />;

    if (model.customThumbnailComponent) imagePreview = model.customThumbnailComponent;

    return (
        <Container
            role='button'
            aria-label={model.title}
            aria-disabled={model.id === disableProductCode}
            aria-pressed={selected}
            onClick={model.id !== disableProductCode ? () => onClick(hideAlreadyConfiguredBadge) : () => null}
            $isDisabled={model.id === disableProductCode}
        >
            {model.isConfiguratedInZakeke && !hideAlreadyConfiguredBadge && <AlreadyImportedBadge>{T._('Already Configured', 'Admin')}</AlreadyImportedBadge>}
            {!model.isConfiguratedInZakeke}

            <ImageContainer $isDisabled={model.id === disableProductCode}>{imagePreview}</ImageContainer>

            {model.customFooter ? (
                model.customFooter
            ) : (
                <CatalogListItemFooter>
                    <CatalogListItemFooterText $isConfiguratedInZakeke={model.isConfiguratedInZakeke} $hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}>
                        <span>{cutString(model.title, 40)}</span>
                        <span>{model.subtitle}</span>
                    </CatalogListItemFooterText>

                    <FooterButtons role='toolbar' aria-label='Catalog item actions'>
                        {model.buttons?.map((x) => {
                            {
                                return (
                                    <FooterButton
                                        key={x.key}
                                        role='button'
                                        aria-label={x.tooltip || `Action button for ${model.title}`}
                                        aria-disabled={!x.isActive}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCatalogListItemButtonClick(x, model);
                                        }}
                                    >
                                        <ButtonIcon tooltip={x.tooltip} $isActive={x.isActive} aria-hidden='true'>
                                            {x.icon}
                                        </ButtonIcon>
                                    </FooterButton>
                                );
                            }
                        })}
                    </FooterButtons>
                </CatalogListItemFooter>
            )}
            {selected && (
                <CheckIcon aria-hidden='true'>
                    <MdCheckCircle />
                </CheckIcon>
            )}
        </Container>
    );
};
